import { Sensor, SensorAlert, SensorAlertSeverity } from "common";
import { useTranslation } from "react-i18next";
import { Heading, Text, YStack } from "tamagui";

import { GradientCard, GradientCardProps } from "./GradientCard";
import { StatusPill } from "./StatusPill";
import { useStatusPillProps } from "../hooks/useStatusPillProps";

export interface SensorCardProps extends GradientCardProps {
  sensor: Sensor & { alerts?: SensorAlert[] };
}

export const SensorCard = ({ sensor, ...rest }: SensorCardProps) => {
  const { t } = useTranslation();

  const sortedAlerts = sensor.alerts
    ? [...sensor.alerts].sort(
        (a, b) =>
          severityOrder.indexOf(a.severity) - severityOrder.indexOf(b.severity),
      )
    : [];
  const alert: SensorAlert = sortedAlerts[0];
  const { generateSensorStatusPillProps } = useStatusPillProps();
  const {
    children: pillText,
    outlined: pillOutlined,
    error,
    warning,
    success,
  } = generateSensorStatusPillProps(sensor.status, alert);

  return (
    <GradientCard error={error} warning={warning} flex={1} {...rest}>
      <StatusPill
        error={error}
        warning={warning}
        success={success}
        outlined={pillOutlined}
      >
        {pillText}
      </StatusPill>
      <YStack gap="$1.5">
        <Heading fontSize="$3" lineHeight="$2" color="$white12">
          {alert?.message ?? "Good"}
        </Heading>
        <Text fontSize="$3.5" color="$white10">
          {sensor.name}
        </Text>
      </YStack>
      <Text fontSize="$3" color="$white8">
        {t("sensors.card.lastUpdated", {
          date: new Date(sensor.lastReported),
        })}
      </Text>
    </GradientCard>
  );
};

const severityOrder = [
  SensorAlertSeverity.Info,
  SensorAlertSeverity.Low,
  SensorAlertSeverity.Medium,
  SensorAlertSeverity.High,
  SensorAlertSeverity.Critical,
];
